import { Loader2, Download, UserPlus, MessageSquareText, Phone, UserCog } from "lucide-react";
import { useReport, exportCsv, fmtWhen, RCard, Tile } from "./shared";
import { homeLabel } from "@/lib/home";

const SOURCE = {
  manager: { label: "Added by manager", icon: UserCog, cls: "bg-sand-100 text-ink-600" },
  self_text: { label: "Self-registered · text", icon: MessageSquareText, cls: "bg-sky-50 text-sky-700 border border-sky-200" },
  self_voice: { label: "Self-registered · voice", icon: Phone, cls: "bg-violet-50 text-violet-700 border border-violet-200" },
};
const STATUS = {
  pending: { label: "Pending approval", cls: "bg-amber-100 text-amber-800 border border-amber-200" },
  approved: { label: "Approved", cls: "bg-emerald-50 text-emerald-700 border border-emerald-200" },
  auto_verified: { label: "Auto-verified", cls: "bg-emerald-50 text-emerald-700 border border-emerald-200" },
  declined: { label: "Declined", cls: "bg-red-50 text-red-700 border border-red-200" },
  added: { label: "Added", cls: "bg-sand-100 text-ink-600" },
};

export const SignupsReport = ({ tenantId, periodKey, customStart, customEnd, reloadKey }) => {
  const { data, loading } = useReport("signups", tenantId, periodKey, customStart, customEnd, reloadKey);

  if (loading) return <div className="flex items-center gap-2 text-ink-500 py-24 justify-center"><Loader2 className="h-5 w-5 animate-spin" /> Building report…</div>;
  if (!data) return null;
  const { tiles, items } = data;

  const downloadCsv = () => exportCsv(
    "new-signups.csv",
    ["Name", "Phone", "Unit", "Address", "How they joined", "Status", "Date"],
    items.map((i) => [i.name, i.phone, i.unit, i.address, SOURCE[i.source]?.label || i.source, STATUS[i.status]?.label || i.status, fmtWhen(i.at)]),
  );

  return (
    <div data-testid="signups-report" className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Tile testid="signups-tile-total" label="New members" value={tiles.total} sub="added this period" />
        <Tile testid="signups-tile-self" label="Self-registered" value={tiles.self_registered} sub="via text or voice" />
        <Tile testid="signups-tile-manager" label="Manager-added" value={tiles.manager_added} sub="by your team" />
        <Tile testid="signups-tile-pending" label="Awaiting approval" value={tiles.pending} sub="in your queue now" />
        <Tile testid="signups-tile-approved" label="Approved" value={tiles.approved}
          sub={tiles.auto_verified ? `${tiles.auto_verified} instant via account #` : `${tiles.declined} declined`} />
      </div>

      <RCard>
        <div className="flex items-center justify-between px-5 pt-5 pb-3">
          <div className="flex items-center gap-2 text-[11px] font-bold tracking-[0.14em] text-ink-400 uppercase">
            <UserPlus className="h-3.5 w-3.5 text-redline-500" strokeWidth={2} /> Every new member
          </div>
          <button data-testid="signups-export-csv" onClick={downloadCsv} disabled={!items.length}
            className="rounded-full border border-sand-200 bg-white px-3.5 py-1.5 text-xs font-semibold text-ink-600 hover:border-ink transition-colors flex items-center gap-1.5 disabled:opacity-40">
            <Download className="h-3.5 w-3.5" strokeWidth={2} /> CSV
          </button>
        </div>
        {!items.length ? (
          <div className="px-5 pb-8 pt-2 text-sm text-ink-500">No new members in this period.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider font-bold text-muted-foreground border-b border-sand-200">
                  <th className="px-5 py-2.5">Name</th>
                  <th className="px-3 py-2.5">Property</th>
                  <th className="px-3 py-2.5">How they joined</th>
                  <th className="px-3 py-2.5">Status</th>
                  <th className="px-5 py-2.5 text-right">When</th>
                </tr>
              </thead>
              <tbody>
                {items.map((i, idx) => {
                  const src = SOURCE[i.source] || SOURCE.manager;
                  const st = STATUS[i.status] || STATUS.added;
                  const SrcIcon = src.icon;
                  return (
                    <tr key={i.person_id || idx} data-testid={`signup-row-${i.person_id || idx}`} className="border-b border-sand-100 last:border-0">
                      <td className="px-5 py-3">
                        <div className="font-semibold text-ink">{i.name}</div>
                        {i.phone && <div className="text-xs text-ink-500">{i.phone}</div>}
                      </td>
                      <td className="px-3 py-3 text-ink-600">{homeLabel(i) || "—"}</td>
                      <td className="px-3 py-3">
                        <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-bold ${src.cls}`}>
                          <SrcIcon className="h-3 w-3" strokeWidth={2} /> {src.label}
                        </span>
                      </td>
                      <td className="px-3 py-3">
                        <span className={`inline-flex rounded-full px-2.5 py-1 text-[11px] font-bold ${st.cls}`}>{st.label}</span>
                      </td>
                      <td className="px-5 py-3 text-right text-ink-500 whitespace-nowrap">{fmtWhen(i.at)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </RCard>
    </div>
  );
};
